import { Interview, Project } from "./interviewService";

// Mock interview data with realistic information
export const mockInterviews: Interview[] = [
  {
    id: "demo-1",
    expertFirstName: "Sarah",
    expertLastName: "Johnson",
    company: "TechCorp Solutions",
    title: "VP of Engineering",
    network: "Coleman",
    date: new Date("2024-01-15T14:00:00Z"),
    duration: "60",
    initialInterviewCost: "800",
    status: "completed",
    angle: "Customer",
    anonymizedAttribution: "Vice President, TechCorp Solutions",
    interviewDescription:
      "01/15_Sarah_Johnson_Vice_President_TechCorp_Solutions",
  },
  {
    id: "demo-2",
    expertFirstName: "Michael",
    expertLastName: "Chen",
    company: "DataFlow Analytics",
    title: "Chief Data Officer",
    network: "Coleman",
    date: new Date("2024-01-18T16:30:00Z"),
    duration: "60",
    initialInterviewCost: "950",
    status: "completed",
    angle: "Competitor",
    anonymizedAttribution: "Chief Officer, DataFlow Analytics",
    interviewDescription: "01/18_Michael_Chen_Chief_Officer_DataFlow_Analytics",
  },
  {
    id: "demo-3",
    expertFirstName: "Amanda",
    expertLastName: "Rodriguez",
    company: "FinanceFirst Bank",
    title: "Head of Digital Transformation",
    network: "AlphaSights",
    date: new Date("2024-01-22T10:00:00Z"),
    duration: "60",
    initialInterviewCost: "750",
    status: "completed",
    angle: "Customer",
    anonymizedAttribution: "Senior Executive, FinanceFirst Bank",
    interviewDescription:
      "01/22_Amanda_Rodriguez_Senior_Executive_FinanceFirst_Bank",
  },
  {
    id: "demo-4",
    expertFirstName: "David",
    expertLastName: "Kim",
    company: "CloudScale Inc",
    title: "Director of Cloud Architecture",
    network: "Guidepoint",
    date: new Date("2024-01-25T15:00:00Z"),
    duration: "60",
    initialInterviewCost: "650",
    status: "scheduled",
    angle: "Competitor",
    anonymizedAttribution: "Director, CloudScale Inc",
    interviewDescription: "01/25_David_Kim_Director_CloudScale_Inc",
  },
  {
    id: "demo-5",
    expertFirstName: "Lisa",
    expertLastName: "Thompson",
    company: "RetailMax Corporation",
    title: "SVP of Operations",
    network: "Guidepoint",
    date: new Date("2024-01-28T13:30:00Z"),
    duration: "60",
    initialInterviewCost: "700",
    status: "scheduled",
    angle: "Customer",
    anonymizedAttribution: "Senior Vice President, RetailMax Corporation",
    interviewDescription:
      "01/28_Lisa_Thompson_Senior_Vice_President_RetailMax_Corporation",
  },
  {
    id: "demo-6",
    expertFirstName: "Robert",
    expertLastName: "Martinez",
    company: "HealthTech Innovations",
    title: "Chief Medical Officer",
    network: "Atheneum",
    date: new Date("2024-02-01T11:00:00Z"),
    duration: "60",
    initialInterviewCost: "1200",
    status: "scheduled",
    angle: "Competitor",
    anonymizedAttribution: "Chief Officer, HealthTech Innovations",
    interviewDescription:
      "02/01_Robert_Martinez_Chief_Officer_HealthTech_Innovations",
  },
  {
    id: "demo-7",
    expertFirstName: "Jennifer",
    expertLastName: "Walsh",
    company: "GreenEnergy Solutions",
    title: "VP of Sustainability",
    network: "AlphaSights",
    date: new Date("2024-02-05T14:30:00Z"),
    duration: "60",
    initialInterviewCost: "850",
    status: "canceled",
    angle: "Competitor",
    anonymizedAttribution: "Vice President, GreenEnergy Solutions",
    interviewDescription:
      "02/05_Jennifer_Walsh_Vice_President_GreenEnergy_Solutions",
  },
  {
    id: "demo-8",
    expertFirstName: "James",
    expertLastName: "Wilson",
    company: "AutoTech Dynamics",
    title: "Head of Autonomous Vehicles",
    network: "Guidepoint",
    date: new Date("2024-02-08T16:00:00Z"),
    duration: "60",
    initialInterviewCost: "900",
    status: "scheduled",
    angle: "Customer",
    anonymizedAttribution: "Senior Executive, AutoTech Dynamics",
    interviewDescription:
      "02/08_James_Wilson_Senior_Executive_AutoTech_Dynamics",
  },
];

// Mock project data
export const mockProjects: Project[] = [
  {
    id: "demo-project-1",
    name: "Digital Transformation in Financial Services",
    description:
      "Research project examining digital transformation strategies across major financial institutions",
    status: "active",
    max_budget: 25000,
    target_n: 12,
    role: "owner",
    client: "Global Finance Corp",
    start_date: "2024-01-15",
  },
  {
    id: "demo-project-2",
    name: "AI Implementation in Healthcare",
    description:
      "Study on artificial intelligence adoption patterns in healthcare organizations",
    status: "active",
    max_budget: 30000,
    target_n: 15,
    role: "editor",
    client: "HealthCare Innovations LLC",
    start_date: "2024-02-01",
  },
  {
    id: "demo-project-3",
    name: "Supply Chain Resilience Post-COVID",
    description:
      "Analysis of supply chain adaptations and resilience strategies following the pandemic",
    status: "archived",
    max_budget: 20000,
    target_n: 10,
    role: "owner",
    client: "Supply Chain Analytics Inc",
    start_date: "2023-11-01",
  },
];

// Dynamic project-to-interview mapping that gets updated when interviews are created
const projectInterviewMap: Record<string, string[]> = {
  "demo-project-1": ["demo-1", "demo-2", "demo-3"],
  "demo-project-2": ["demo-4", "demo-5", "demo-6"],
  "demo-project-3": ["demo-7", "demo-8"],
};

// Demo service that mimics the real API calls
export const demoService = {
  // Authentication methods (always return demo user as authenticated)
  getAuthStatus: async () => {
    return {
      isAuthenticated: true,
      isAuthorized: true,
      userName: "Demo User",
      userDetails: {
        name: "Demo User",
        email: "demo@example.com",
      },
    };
  },

  signOut: async () => {
    // In demo mode, just redirect to main site
    window.location.href = "/signin";
    return Promise.resolve({ success: true });
  },

  // Interview methods
  getAllInterviews: async (): Promise<Interview[]> => {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    console.log(
      "Demo: getAllInterviews called, returning",
      mockInterviews.length,
      "interviews"
    );
    console.log(
      "Demo: Interview IDs:",
      mockInterviews.map((i) => i.id)
    );
    return [...mockInterviews];
  },

  getInterviewsByProject: async (projectId: string): Promise<Interview[]> => {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Filter interviews by project - for demo, we'll assign interviews to projects
    const interviewIds = projectInterviewMap[projectId] || [];
    const filteredInterviews = mockInterviews.filter((interview) =>
      interviewIds.includes(interview.id)
    );

    console.log("Demo: getInterviewsByProject called for project:", projectId);
    console.log("Demo: Expected interview IDs:", interviewIds);
    console.log("Demo: Total mockInterviews:", mockInterviews.length);
    console.log("Demo: Filtered interviews:", filteredInterviews.length);
    console.log(
      "Demo: All mockInterview IDs:",
      mockInterviews.map((i) => i.id)
    );

    return filteredInterviews;
  },

  updateInterviews: async (interviews: Interview[]): Promise<boolean> => {
    await new Promise((resolve) => setTimeout(resolve, 800));

    // Actually update the mock interviews data
    console.log("Demo: Updating interviews:", interviews);

    // Replace the mock interviews with the updated ones
    mockInterviews.length = 0; // Clear existing interviews
    mockInterviews.push(...interviews); // Add updated interviews

    return true;
  },

  updateInterview: async (interview: Interview): Promise<boolean> => {
    await new Promise((resolve) => setTimeout(resolve, 400));

    // Find and update the specific interview in mock data
    const index = mockInterviews.findIndex((i) => i.id === interview.id);
    if (index !== -1) {
      mockInterviews[index] = interview;
      console.log("Demo: Updated interview:", interview.id);
      return true;
    } else {
      console.log("Demo: Interview not found for update:", interview.id);
      return false;
    }
  },

  createInterview: async (
    interview: Partial<Interview>,
    projectId: string
  ): Promise<Interview | null> => {
    await new Promise((resolve) => setTimeout(resolve, 600));

    const newInterview: Interview = {
      id: `demo-new-${Date.now()}`,
      expertFirstName: interview.expertFirstName || "",
      expertLastName: interview.expertLastName || "",
      company: interview.company || "",
      title: interview.title || "",
      network: interview.network || "",
      date: interview.date,
      duration: interview.duration || "30",
      initialInterviewCost: interview.initialInterviewCost || "0",
      status: interview.status || "scheduled",
      angle: interview.angle || "",
      anonymizedAttribution: interview.anonymizedAttribution || "",
      interviewDescription: interview.interviewDescription || "",
    };

    // Actually add the new interview to the mock data so it persists
    mockInterviews.push(newInterview);

    // Add the new interview to the current project's interview list
    if (!projectInterviewMap[projectId]) {
      projectInterviewMap[projectId] = [];
    }
    projectInterviewMap[projectId].push(newInterview.id);

    console.log(
      "Demo: Created and added new interview:",
      newInterview.id,
      "to project:",
      projectId
    );

    return newInterview;
  },

  deleteInterview: async (interviewId: string): Promise<boolean> => {
    await new Promise((resolve) => setTimeout(resolve, 400));

    // Actually remove the interview from mock data
    const initialLength = mockInterviews.length;
    const index = mockInterviews.findIndex(
      (interview) => interview.id === interviewId
    );

    if (index !== -1) {
      mockInterviews.splice(index, 1);
      console.log("Demo: Deleted interview:", interviewId);
      return true;
    } else {
      console.log("Demo: Interview not found for deletion:", interviewId);
      return false;
    }
  },

  // Project methods
  getOwnedProjects: async (): Promise<Project[]> => {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockProjects.filter((p) => p.role === "owner");
  },

  getSharedProjects: async (): Promise<Project[]> => {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockProjects.filter((p) => p.role === "editor");
  },

  getArchivedProjects: async (): Promise<Project[]> => {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockProjects.filter((p) => p.status === "archived");
  },

  toggleProjectStatus: async (
    projectId: string,
    newStatus: string
  ): Promise<boolean> => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    console.log("Demo: Would toggle project status:", projectId, newStatus);
    return true;
  },

  createProject: async (projectData: any): Promise<Project | null> => {
    await new Promise((resolve) => setTimeout(resolve, 700));

    const newProject: Project = {
      id: `demo-project-new-${Date.now()}`,
      name: projectData.name,
      description: projectData.description,
      status: projectData.status || "active",
      max_budget: projectData.max_budget,
      target_n: projectData.target_n,
      role: "owner",
      client: projectData.client,
      start_date: projectData.start_date,
    };

    return newProject;
  },

  updateProject: async (
    projectId: string,
    projectData: any
  ): Promise<Project | null> => {
    await new Promise((resolve) => setTimeout(resolve, 600));
    console.log("Demo: Would update project:", projectId, projectData);

    const existingProject = mockProjects.find((p) => p.id === projectId);
    if (existingProject) {
      return { ...existingProject, ...projectData };
    }
    return null;
  },

  // Calendar sync
  syncCalendarEvents: async (): Promise<{
    success: boolean;
    message: string;
    addedCount: number;
    updatedCount?: number;
    cancelledCount?: number;
  }> => {
    await new Promise((resolve) => setTimeout(resolve, 4000));

    // Add some new mock interviews to simulate calendar sync
    const newInterviews: Interview[] = [
      {
        id: `demo-sync-${Date.now()}-1`,
        expertFirstName: "Alex",
        expertLastName: "Turner",
        company: "InnovateTech Systems",
        title: "CTO",
        network: "Guidepoint",
        date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week from now
        duration: "60",
        initialInterviewCost: "1100",
        status: "scheduled",
        angle: "Competitor",
        anonymizedAttribution: "Chief Officer, InnovateTech Systems",
        interviewDescription: `${String(
          new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).getMonth() + 1
        ).padStart(2, "0")}/${String(
          new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).getDate()
        ).padStart(2, "0")}_Alex_Turner_Chief_Officer_InnovateTech_Systems`,
      },
      {
        id: `demo-sync-${Date.now()}-2`,
        expertFirstName: "Maria",
        expertLastName: "Garcia",
        company: "ConsumerGoods Plus",
        title: "VP of Marketing",
        network: "Atheneum",
        date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000), // 10 days from now
        duration: "60",
        initialInterviewCost: "650",
        status: "scheduled",
        angle: "Customer",
        anonymizedAttribution: "Vice President, ConsumerGoods Plus",
        interviewDescription: `${String(
          new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).getMonth() + 1
        ).padStart(2, "0")}/${String(
          new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).getDate()
        ).padStart(2, "0")}_Maria_Garcia_Vice_President_ConsumerGoods_Plus`,
      },
    ];

    // Add the new interviews to the mock data
    mockInterviews.push(...newInterviews);

    // Add the new interview IDs to the first active project (demo-project-1)
    const targetProjectId = "demo-project-1";
    if (!projectInterviewMap[targetProjectId]) {
      projectInterviewMap[targetProjectId] = [];
    }
    newInterviews.forEach((interview) => {
      projectInterviewMap[targetProjectId].push(interview.id);
    });

    console.log(
      "Demo: Synced",
      newInterviews.length,
      "interviews and assigned to project:",
      targetProjectId
    );

    // Also simulate updating an existing interview (mark one as updated)
    const existingInterview = mockInterviews.find(
      (interview) => interview.status === "scheduled"
    );
    if (existingInterview) {
      existingInterview.duration = "30"; // Simulate a duration change from calendar
    }

    return {
      success: true,
      message: "Demo sync completed successfully!",
      addedCount: 2,
      updatedCount: 1,
      cancelledCount: 0,
    };
  },

  // AI services
  generateAnonymizedAttribution: async (
    title: string,
    company: string
  ): Promise<string> => {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    const templates = [
      `Former ${title} at a ${
        company.toLowerCase().includes("tech") ? "technology" : "leading"
      } company`,
      `${title} at a major ${
        company.toLowerCase().includes("bank")
          ? "financial institution"
          : "corporation"
      }`,
      `Senior executive with ${title.toLowerCase()} experience at ${
        company.toLowerCase().includes("healthcare")
          ? "healthcare organizations"
          : "industry-leading companies"
      }`,
    ];
    return templates[Math.floor(Math.random() * templates.length)];
  },

  generateInterviewDescription: (
    date: Date | string | undefined,
    firstName: string,
    lastName: string,
    anonymizedAttribution: string
  ): string => {
    // Format date as mm/dd
    let formattedDate = "";
    if (date) {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      if (!isNaN(dateObj.getTime())) {
        const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
        const day = dateObj.getDate().toString().padStart(2, "0");
        formattedDate = `${month}/${day}`;
      }
    }

    // Clean up names for filename
    const cleanFirstName = firstName.replace(/[^a-zA-Z0-9]/g, "");
    const cleanLastName = lastName.replace(/[^a-zA-Z0-9]/g, "");

    // Parse anonymized attribution to separate title and company
    // Expected format: "VP of Engineering, TechCorp Solutions" -> "VP_of_Engineering_TechCorp_Solutions"
    let cleanAttribution = "";
    if (anonymizedAttribution.includes(",")) {
      const parts = anonymizedAttribution.split(",").map((part) => part.trim());
      const cleanTitle = parts[0]
        .replace(/[^a-zA-Z0-9\s]/g, "")
        .replace(/\s+/g, "_");
      const cleanCompany = parts[1]
        ? parts[1].replace(/[^a-zA-Z0-9\s]/g, "").replace(/\s+/g, "_")
        : "";
      cleanAttribution = cleanCompany
        ? `${cleanTitle}_${cleanCompany}`
        : cleanTitle;
    } else {
      // Fallback if no comma found
      cleanAttribution = anonymizedAttribution
        .replace(/[^a-zA-Z0-9\s]/g, "")
        .replace(/\s+/g, "_");
    }

    // Construct the description
    const parts = [
      formattedDate,
      cleanFirstName,
      cleanLastName,
      cleanAttribution,
    ].filter(Boolean); // Remove empty parts

    return parts.join("_");
  },

  batchGenerateAnonymizedAttributions: async (
    interviews: Array<{ id: string; title: string; company: string }>
  ): Promise<Record<string, string>> => {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const result: Record<string, string> = {};

    for (const interview of interviews) {
      result[interview.id] = await demoService.generateAnonymizedAttribution(
        interview.title,
        interview.company
      );
    }

    return result;
  },

  parseCalendarText: async (
    calendarText: string,
    projectId?: string
  ): Promise<any> => {
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Mock parsing logic for demo - extract some basic information
    const lowerText = calendarText.toLowerCase();

    // Try to extract some demo data
    const mockData = {
      expertFirstName: lowerText.includes("john") ? "John" : "Sarah",
      expertLastName: lowerText.includes("smith") ? "Smith" : "Johnson",
      company: lowerText.includes("microsoft")
        ? "Microsoft"
        : lowerText.includes("google")
        ? "Google"
        : lowerText.includes("apple")
        ? "Apple"
        : "TechCorp Solutions",
      title: lowerText.includes("director")
        ? "Director of Engineering"
        : lowerText.includes("vp") || lowerText.includes("vice president")
        ? "VP of Operations"
        : "Senior Manager",
      angle: lowerText.includes("competitor") ? "Competitor" : "Customer",
      network: lowerText.includes("guidepoint")
        ? "Guidepoint"
        : lowerText.includes("alphasights")
        ? "AlphaSights"
        : "Coleman",
      date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week from now
      duration: lowerText.includes("30") ? "30" : "60",
      initialInterviewCost: lowerText.includes("800") ? "800" : "750",
      anonymizedAttribution: "",
    };

    // Generate anonymized attribution
    mockData.anonymizedAttribution =
      await demoService.generateAnonymizedAttribution(
        mockData.title,
        mockData.company
      );

    return mockData;
  },

  calculatePrice: async (
    network: string,
    duration: string | number,
    price?: string | number,
    currentTotal?: string | number,
    currentDuration?: string | number
  ): Promise<number> => {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const dur = Number(duration) || 60;
    // Simple demo heuristic: $10 per minute unless explicit price provided
    if (price !== undefined) {
      return Number(price) || Math.round(dur * 10);
    }
    return Math.round(dur * 10);
  },

  // User profile methods (demo versions)
  getUserProfile: async (): Promise<{
    user_id: string;
    name: string;
    email: string;
    last_calendar_sync?: string;
  }> => {
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Mock last sync time - 2 hours ago
    const lastSync = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();

    return {
      user_id: "demo-user-1",
      name: "Demo User",
      email: "demo@example.com",
      last_calendar_sync: lastSync,
    };
  },

  updateUserSyncTime: async (
    lastSyncTime: string
  ): Promise<{
    success: boolean;
    last_calendar_sync: string;
  }> => {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    return {
      success: true,
      last_calendar_sync: lastSyncTime,
    };
  },
};
